<template>
  <div
    :class="{
      'export-center': true,
      'export-center-detail': popoverType === 1,
    }"
  >
    <s-popover
      ref="popover"
      placement="bottom"
      trigger="click"
      :append-to-body="false"
      popper-class="export-popover"
      @show="onShowPopover"
      @hide="onHidePopover"
      :width="popoverType === 1 ? 480 : undefined"
    >
      <div
        slot="reference"
        :class="{ 'export-reference': true, 'active': visible }"
        @click="handleClick"
      >
        <s-icon-order-outline class="export-icon" />
        <span v-if="popoverType === 2" class="red-dot"></span>
      </div>
      <div slot="default">
        <div v-if="popoverType === 1" class="export-tasks">
          <div class="tasks-header">
            <div class="tasks-header-left">
              <p class="title">Lastest Task</p>
              <p class="desc">
                Only show the tasks of the latest 7 days, Click the "View All"
                button to view more details
              </p>
            </div>
            <s-button @click="handleViewAll">View All</s-button>
          </div>
          <div class="tasks-line" />
          <div
            class="tasks-list"
            v-if="!!exportTasks?.length"
            v-loading="searchLoading"
          >
            <div
              class="tasks-item"
              v-for="task in exportTasks"
              :key="task.task_id"
            >
              <div class="item-info">
                <img src="@/assets/icons/export.svg" />
                <div class="info-text">
                  <s-tooltip placement="top" :content="task.export_file_name">
                    <p class="info-name">
                      {{ task.export_file_name | emptyFormat }}
                    </p>
                  </s-tooltip>
                  <p class="info-time">{{ task.ctime | format }}</p>
                </div>
              </div>
              <div class="item-result">
                <template v-if="task.task_status === ExportTaskStatus.DONE">
                  <s-tag type="success">
                    <template v-slot:icon>
                      <s-icon-success-filled />
                    </template>
                    {{ $i18n('Succeeded') }}
                  </s-tag>
                  <s-button
                    type="primary"
                    :plain="true"
                    size="small"
                    @click="handleDownload(task)"
                  >
                    {{ $i18n('Download') }}</s-button
                  >
                </template>
                <template
                  v-else-if="task.task_status === ExportTaskStatus.FAIL"
                >
                  <s-tag type="danger">
                    <template v-slot:icon>
                      <s-icon-error-filled />
                    </template>
                    {{ $i18n('Failed') }}
                  </s-tag>
                  <s-button
                    type="primary"
                    :plain="true"
                    size="small"
                    @click="handleRetry(task)"
                  >
                    {{ $i18n('Retry') }}</s-button
                  >
                </template>
                <template v-else>
                  <s-progress
                    :length="80"
                    :stroke-width="4"
                    :percent="task.processed_percentage ?? 80"
                  />
                </template>
              </div>
            </div>
          </div>
          <div v-else class="export-empty">
            <s-empty :description="$i18n('No Data')" />
          </div>
        </div>
        <span v-else class="export-tips">{{ tips }}</span>
      </div>
    </s-popover>
  </div>
</template>

<script lang="ts">
import {
  retryExportTask,
  retryRTInboundExportTask,
  searchExportTask,
} from '@/api/wms-config/download-center';
import { EXPORT_POPOVER_EVENT } from '@/common/constant';
import { ExportTaskStatus } from '@/ConstEnums';
import { EventBus } from '@/i18n';
import { errorReport } from '@/utils/tools';
import { ExportTaskItem } from '@/views/report-center/report-center/index.vue';
import { Component, Vue } from 'vue-property-decorator';

@Component({
  components: {},
})
export default class ExportCenter extends Vue {
  ExportTaskStatus = ExportTaskStatus;
  visible = false;
  popoverType = 1; // 1: 详情 2: 文本
  exportTasks: ExportTaskItem[] = [];
  pollingTimer: NodeJS.Timeout | null = null;
  searchLoading = false;
  watchingTaskIDs = new Set<string>();
  succeededTaskIDs = new Set<string>();
  failedTaskIDs = new Set<string>();
  hideTimer: NodeJS.Timeout | null = null;

  get tips() {
    let succeededTips = '',
      failedTips = '';
    console.log(this.succeededTaskIDs.size, this.failedTaskIDs.size);
    if (this.succeededTaskIDs.size) {
      succeededTips = `${this.succeededTaskIDs.size} export tasks succeeded`;
    }
    if (this.failedTaskIDs.size) {
      failedTips = `${this.failedTaskIDs.size} export tasks failed`;
    }
    if (succeededTips && failedTips) {
      return succeededTips + '\n' + failedTips;
    }
    return succeededTips || failedTips;
  }

  async getExportTasks() {
    try {
      this.searchLoading = true;
      // latest 7 days
      const endTime = Math.floor(new Date().getTime() / 1000);
      const startTime = Math.floor(endTime - 7 * 86400);
      const res = await searchExportTask({
        pageno: 1,
        count: 9999,
        is_myself: 1,
        start_time: startTime,
        end_time: endTime,
      });
      this.exportTasks = res.data?.list ?? [];
      this.handleExportTasksStatus();
    } catch (error) {
      errorReport(error);
    } finally {
      this.searchLoading = false;
    }
  }

  handleExportTasksStatus() {
    let hasInProgressTask = false;
    this.exportTasks.forEach((task) => {
      if (
        [ExportTaskStatus.PENDING, ExportTaskStatus.ONGOING].includes(
          task.task_status
        )
      ) {
        hasInProgressTask = true;
        if (!this.visible) {
          this.watchingTaskIDs.add(task.task_id);
        }
      } else {
        if (this.watchingTaskIDs.has(task.task_id)) {
          this.watchingTaskIDs.delete(task.task_id);
          if (!this.visible) {
            if (task.task_status === ExportTaskStatus.DONE) {
              this.succeededTaskIDs.add(task.task_id);
            } else {
              this.failedTaskIDs.add(task.task_id);
            }
            this.popoverType = 2;
            // 红点提示，同时出现 popover 文字提示，3s 后自动消失
            this.$nextTick(() => {
              (this.$refs.popover as any)?.showPopover();
              this.hideTimer && clearTimeout(this.hideTimer);
              this.hideTimer = setTimeout(() => {
                (this.$refs.popover as any)?.hidePopover();
              }, 3000);
            });
          }
        }
      }
    });
    // 存在未完成的导出任务时开始轮询更新状态
    if (hasInProgressTask) {
      if (this.pollingTimer) {
        return;
      }
      this.pollingTimer = setTimeout(() => {
        this.pollingTimer = null;
        this.getExportTasks();
      }, 6000);
    }
  }

  onShowPopover() {
    this.visible = true;
    if (this.popoverType === 1) {
      this.getExportTasks();
    }
  }

  onHidePopover() {
    this.visible = false;
  }

  onShowPopoverDetail() {
    this.popoverType = 1;
    this.$nextTick(() => {
      (this.$refs.popover as any)?.showPopover();
    });
  }

  handleClick() {
    if (this.popoverType === 2) {
      this.popoverType = 1;
    }
  }

  handleViewAll() {
    (this.$refs.popover as any).hidePopover();
    this.$router.push('/reportcenter/reportcenter');
    this.visible = false;
  }

  handleDownload(task: ExportTaskItem) {
    window.open(task.download_link);
  }

  async handleRetry(task: ExportTaskItem) {
    try {
      const params = {
        task_id: task.task_id,
      };

      //退货入库采用新接口
      if (
        task.export_module === 11 &&
        this.$store.state.warehouseDetail.warehouse.is_rms
      ) {
        await retryRTInboundExportTask(params);
      } else {
        await retryExportTask(params);
      }

      this.$message.success(this.$i18n('Retry Success!'));
      this.getExportTasks();
    } catch (e) {
      this.$message.warning(
        this.$i18n('Retry failed! Please try again later.')
      );
      errorReport(e);
    }
  }

  mounted() {
    this.getExportTasks();
    EventBus.$on(EXPORT_POPOVER_EVENT, this.onShowPopoverDetail);
  }

  beforeDestroy() {
    this.pollingTimer && clearTimeout(this.pollingTimer);
    this.pollingTimer = null;
    EventBus.$off(EXPORT_POPOVER_EVENT, this.onShowPopoverDetail);
  }
}
</script>

<style lang="scss" scoped>
$active-color: rgba(
  $color: #000000,
  $alpha: 0.04,
);
.export-center {
  margin: 6px 16px 6px 0;

  // 显示 task 详情时使用
  &.export-center-detail {
    ::v-deep .export-popover {
      padding: 0;
    }
  }

  .export-reference {
    position: relative;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    &:hover {
      background-color: $active-color;
    }
    &:active {
      background-color: $active-color;
    }
    &.active {
      background-color: $active-color;
    }
    .export-icon {
      width: 16px;
    }
    .red-dot {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 6px;
      height: 6px;
      background-color: #ee4d2d;
      border-radius: 50%;
    }
  }
  .export-tips {
    white-space: pre-line;
  }

  .export-tasks {
    display: flex;
    flex-direction: column;
    width: 480px;
    height: 330px;

    .tasks-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;

      .title {
        font-size: 18px;
        font-weight: 500;
      }
      .desc {
        max-width: 280px;
        font-size: 12px;
        color: #999;
        line-height: 16px;
        margin-top: 8px;
      }
    }
    .tasks-line {
      width: 100%;
      height: 2px;
      background-color: #f0f2f5;
    }
    .tasks-list {
      flex: 1;
      overflow-y: auto;
      .tasks-item {
        height: 70px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        border-bottom: 1px solid #eee;

        .item-info {
          display: flex;
          align-items: center;
          .info-text {
            margin-left: 4px;
          }
          .info-name {
            color: #303844;
            max-width: 200px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .info-time {
            font-size: 12px;
            color: #999;
          }
        }
        .item-result {
          display: flex;
          align-items: center;
          ::v-deep .ssc-tag {
            display: flex;
          }
        }
      }
    }
    .export-empty {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
}
</style>
